<template>
  <div class="user-detail">
    <APageHead title="강사·학생 상세" :back="true" @back="$router.back()" />

    <div class="user-detail__layout">
      <aside class="user-detail__side">
        <SideNav :sections="userSideNav" />
      </aside>
      <main class="user-detail__main">
        <div v-if="loading" class="user-detail__loading">
          <div class="p-spinner p-spinner--lg"></div>
          <p>사용자 정보를 불러오는 중...</p>
        </div>

        <template v-else-if="userData">
          <BasicInfoCard
            id="basic-info"
            :basic-data="userData"
            user-type="user"
            @update-field="handleUpdateField"
            @save-changes="handleSaveChanges"
          />
          <AccountStatusCard
            id="account-status"
            :current-status="userData.active_yn"
            :is-employee="false"
            @deactivate-account="handleDeactivateAccount"
            @activate-account="handleActivateAccount"
            @delete-account="handleDeleteAccount"
          />
          <WithdrawalInfoCard
            v-if="isWithdrawn"
            id="withdrawal-info"
            :record="withdrawal"
            :loading="withdrawalLoading"
          />
        </template>

        <AEmpty v-else title="사용자 정보를 찾을 수 없어요" sub="존재하지 않는 사용자이거나 권한이 없습니다." />
      </main>
    </div>
  </div>
</template>

<script>
import SideNav from "@/views/users/components/SideNav.vue";
import BasicInfoCard from "@/views/users/components/BasicInfoCard.vue";
import AccountStatusCard from "@/views/users/components/AccountStatusCard.vue";
import WithdrawalInfoCard from "@/views/users/components/WithdrawalInfoCard.vue";
import APageHead from "@/components/Pineapple/APageHead.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import { parseApiError } from "@/utils/apiError";

export default {
  name: "UserDetail",
  components: { SideNav, BasicInfoCard, AccountStatusCard, WithdrawalInfoCard, APageHead, AEmpty },
  data() {
    return {
      user_idx: null,
      userData: null,
      loading: false,
      updateFields: {},
      // 탈퇴(D) 계정의 탈퇴 기록. GET /roles/users/{idx}/withdrawal 이 404 면 null 로 둡니다.
      withdrawal: null,
      withdrawalLoading: false,
    };
  },
  computed: {
    isWithdrawn() {
      return this.userData?.active_yn === "D";
    },
    userSideNav() {
      const nav = [
        { id: "basic-info", icon: "receipt_long", label: "기본 정보" },
        { id: "account-status", icon: "verified_user", label: "계정 상태" },
      ];
      if (this.isWithdrawn) nav.push({ id: "withdrawal-info", icon: "person_off", label: "탈퇴 정보" });
      return nav;
    },
  },
  created() {
    this.user_idx = this.$route.query.idx;
    this.loadUserData();
  },
  methods: {
    async loadUserData() {
      this.loading = true;
      try {
        const res = await this.$axios({
          method: "get",
          url: `/roles/users/${this.user_idx}`,
          loadingBar: true,
        });
        if (res.status === 200) this.userData = res.data;
      } catch (error) {
        console.error("사용자 정보 로드 실패:", error);
        alert("사용자 정보를 불러오는데 실패했습니다.");
      } finally {
        this.loading = false;
      }
      if (this.isWithdrawn) await this.loadWithdrawal();
    },
    // 탈퇴 기록 도입 이전에 탈퇴한 계정은 404 가 정상이라 조용히 "기록 없음" 으로 보여줍니다.
    async loadWithdrawal() {
      this.withdrawalLoading = true;
      try {
        const res = await this.$axios({
          method: "get",
          url: `/roles/users/${this.user_idx}/withdrawal`,
          loadingBar: false,
        });
        this.withdrawal = res.status === 200 ? res.data : null;
      } catch (error) {
        this.withdrawal = null;
        if (error.response?.status !== 404) console.error("탈퇴 기록 로드 실패:", error);
      } finally {
        this.withdrawalLoading = false;
      }
    },
    handleUpdateField({ field, value }) {
      this.updateFields = { ...this.updateFields, [field]: value };
    },
    async handleSaveChanges() {
      if (Object.keys(this.updateFields).length === 0) {
        alert("변경된 내용이 없습니다.");
        return;
      }
      try {
        await this.$axios({
          method: "put",
          url: `/roles/users/${this.user_idx}`,
          data: this.updateFields,
        });
        alert("사용자 정보가 성공적으로 수정되었습니다.");
        this.updateFields = {};
        await this.loadUserData();
      } catch (error) {
        const msg = error.response?.data?.detail || error.response?.data?.message || "사용자 정보 수정에 실패했습니다.";
        alert(msg);
      }
    },
    async patchStatus(status, reason, message) {
      try {
        await this.$axios({
          method: "patch",
          url: `/roles/users/${this.user_idx}/status`,
          data: { status, reason },
          loadingBar: true,
        });
        alert(message);
        await this.loadUserData();
      } catch (error) {
        // 활성 기관 Master 는 탈퇴(D) 시킬 수 없습니다(docs/바우처_기관발행_관리자화면_가이드.md §5).
        // detail 이 객체라 그대로 alert 하면 [object Object] 가 뜹니다. 막힌 기관을 적고 해제 화면으로 안내합니다.
        // 본문이 detail 아래로 오든 최상위 {code, organizations, message} 로 오든 받습니다(같은 가이드 §5).
        const body = error.response?.data;
        const detail = body?.detail && typeof body.detail === "object" ? body.detail : body;
        if (status === "D" && detail?.code === "WITHDRAWAL_BLOCKED_ORG_MASTER") {
          const orgs = (detail.organizations || []).map((o) => o.name).filter(Boolean).join(", ");
          const go = window.confirm(
            "이 계정은 활성 기관의 Master 라 탈퇴 처리할 수 없습니다." +
              (orgs ? `\n기관: ${orgs}` : "") +
              "\n\n바우처 › 기관 Master 관리에서 먼저 해제해 주세요. 지금 이동할까요?"
          );
          if (go) this.$router.push({ name: "VoucherMasters", query: { q: this.userData?.email || "" } });
          return;
        }
        alert(parseApiError(body?.detail, "처리에 실패했습니다."));
      }
    },
    handleDeactivateAccount() {
      if (window.confirm("정말 이 계정을 비활성화하시겠습니까?"))
        this.patchStatus("N", "비활성화 요청", "계정이 비활성화되었습니다.");
    },
    handleActivateAccount() {
      if (window.confirm("정말 이 계정을 활성화하시겠습니까?"))
        this.patchStatus("Y", "활성화 요청", "계정이 활성화되었습니다.");
    },
    handleDeleteAccount() {
      if (window.confirm("정말 이 계정을 삭제하시겠습니까?"))
        this.patchStatus("D", "탈퇴 요청", "계정이 삭제되었습니다.");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.user-detail {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__layout {
    display: grid;
    grid-template-columns: 240px 1fr;
    gap: 16px;

    @media (max-width: 992px) { grid-template-columns: 1fr; }
  }

  &__side { min-width: 0; }

  &__main {
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-width: 0;
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 64px 24px;
    color: $p-ink-mid;
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-lg;

    p { margin: 0; font-size: 12px; }
  }
}
</style>
